import { create } from 'zustand';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { DEFAULT_SHOP_SETTINGS } from './defaultShopSettings';

const SETTINGS_ID = 'DGP-ALBANY';
const LS_KEY = 'shopSettings_v1';

const client = generateClient();

const gqlGet = /* GraphQL */`
  query GetShopSettings($id: ID!) {
    getShopSettings(id: $id) { id currentRO settingsJson }
  }
`;
const gqlCreate = /* GraphQL */`
  mutation CreateShopSettings($input: CreateShopSettingsInput!) {
    createShopSettings(input: $input) { id settingsJson }
  }
`;
const gqlUpdate = /* GraphQL */`
  mutation UpdateShopSettings($input: UpdateShopSettingsInput!) {
    updateShopSettings(input: $input) { id settingsJson }
  }
`;

export function applyTheme(branding) {
  const r = document.documentElement;
  r.style.setProperty('--tt-blue', branding.primaryColor);
  r.style.setProperty('--tt-navy-950', branding.primaryColor);
  r.style.setProperty('--tt-gold', branding.accentColor);
  r.style.setProperty('--tt-gold-hover', shiftBrightness(branding.accentColor, -20));
  r.style.setProperty('--tt-amber', branding.accentColor);
  r.style.setProperty('--tt-green', branding.successColor);
  r.style.setProperty('--tt-red', branding.dangerColor);
}

// Returns PIPELINE filtered, sorted, and label-overridden by flow column settings.
// Always includes PIPELINE stages not yet in saved settings (handles new stages added to PIPELINE).
export function getConfiguredPipeline(pipeline, flowCols) {
  if (!flowCols?.length) return pipeline;

  const colMap = new Map(flowCols.map(c => [c.id, c]));

  // Supplement with any pipeline stages added after settings were last saved
  const allCols = [
    ...flowCols,
    ...pipeline
      .filter(s => !colMap.has(s.id))
      .map((s, i) => ({ id: s.id, label: s.label, visible: true, order: flowCols.length + i })),
  ];

  return allCols
    .filter(col => col.visible !== false)
    .sort((a, b) => a.order - b.order)
    .map(col => {
      const stage = pipeline.find(s => s.id === col.id);
      if (!stage) return null;
      return { ...stage, label: col.label || stage.label };
    })
    .filter(Boolean);
}

function shiftBrightness(hex, delta) {
  const n = parseInt(hex.replace('#', ''), 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const r = clamp((n >> 16) + delta);
  const g = clamp(((n >> 8) & 0xff) + delta);
  const b = clamp((n & 0xff) + delta);
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

function merge(saved) {
  const def = DEFAULT_SHOP_SETTINGS;
  if (!saved) {
    return {
      ...def,
      business: { ...def.business },
      branding: { ...def.branding },
      flow: { columns: def.flow.columns.map(c => ({ ...c })) },
      timers: { ...def.timers, stageThresholds: { ...def.timers.stageThresholds } },
      priority: {
        ...def.priority,
        customerTypeWeights: { ...def.priority.customerTypeWeights },
      },
      commandCenter: {
        ...def.commandCenter,
        widgets: def.commandCenter.widgets.map(w => ({ ...w })),
      },
      pricing: { ...def.pricing },
      accounting: {
        ...def.accounting,
        items: { ...def.accounting.items },
        accounts: { ...def.accounting.accounts },
      },
      catalog: { categories: [...def.catalog.categories], items: [] },
      subscription: null,
    };
  }
  return {
    ...def,
    ...saved,
    business: { ...def.business, ...(saved.business || {}) },
    branding: { ...def.branding, ...(saved.branding || {}) },
    flow: {
      ...def.flow,
      ...(saved.flow || {}),
      columns: saved.flow?.columns || def.flow.columns.map(c => ({ ...c })),
    },
    timers: {
      ...def.timers,
      ...(saved.timers || {}),
      stageThresholds: {
        ...def.timers.stageThresholds,
        ...(saved.timers?.stageThresholds || {}),
      },
    },
    priority: {
      ...def.priority,
      ...(saved.priority || {}),
      customerTypeWeights: {
        ...def.priority.customerTypeWeights,
        ...(saved.priority?.customerTypeWeights || {}),
      },
    },
    commandCenter: {
      ...def.commandCenter,
      ...(saved.commandCenter || {}),
      widgets: saved.commandCenter?.widgets || def.commandCenter.widgets.map(w => ({ ...w })),
    },
    pricing: { ...def.pricing, ...(saved.pricing || {}) },
    accounting: {
      ...def.accounting,
      ...(saved.accounting || {}),
      items:    { ...def.accounting.items,    ...(saved.accounting?.items    || {}) },
      accounts: { ...def.accounting.accounts, ...(saved.accounting?.accounts || {}) },
    },
    catalog: {
      categories: saved.catalog?.categories ?? def.catalog.categories,
      items: saved.catalog?.items ?? [],
    },
    subscription: saved.subscription ?? null,
  };
}

function stripRuntimeFields(settings) {
  return {
    ...settings,
    branding: {
      ...settings.branding,
      logoUrl: null,
    },
  };
}

async function resolveLogoUrl(settings) {
  const key = settings?.branding?.logoKey;
  if (!key) return settings;
  try {
    const path = key.startsWith('public/') ? key : `public/${key}`;
    const { url } = await getUrl({ path });
    return {
      ...settings,
      branding: {
        ...settings.branding,
        logoUrl: url.toString(),
        logoBase64: null,
      },
    };
  } catch (e) {
    console.warn('Failed to resolve shop logo URL:', e.message);
    return settings;
  }
}

function parseSettingsJson(raw) {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

const useShopSettings = create((set, get) => ({
  settings: merge(null),
  settingsExists: null,
  loaded: false,

  load: async () => {
    if (get().loaded) return;
    set({ loaded: true }); // Prevent concurrent calls from both running

    // Apply cached settings immediately so theme is correct on first render
    try {
      const cached = localStorage.getItem(LS_KEY);
      if (cached) {
        const parsed = merge(JSON.parse(cached));
        set({ settings: parsed });
        applyTheme(parsed.branding);
      }
    } catch {}

    // Sync from backend
    try {
      const res = await client.graphql({ query: gqlGet, variables: { id: SETTINGS_ID } });
      const rec = res.data?.getShopSettings;
      if (rec) {
        set({ settingsExists: true });
        const parsed = parseSettingsJson(rec.settingsJson);
        if (parsed) {
          const next = await resolveLogoUrl(merge(parsed));
          set({ settings: next });
          applyTheme(next.branding);
          localStorage.setItem(LS_KEY, JSON.stringify(stripRuntimeFields(next)));
        }
      } else {
        set({ settingsExists: false });
      }
    } catch {
      // Backend unavailable — localStorage already applied
    }
  },

  save: async (newSettings) => {
    const next = await resolveLogoUrl(merge(newSettings));
    set({ settings: next });
    applyTheme(next.branding);
    localStorage.setItem(LS_KEY, JSON.stringify(stripRuntimeFields(next)));

    try {
      const json = JSON.stringify(stripRuntimeFields(next));
      if (get().settingsExists === false) {
        await client.graphql({ query: gqlCreate, variables: { input: { id: SETTINGS_ID, settingsJson: json } } });
        set({ settingsExists: true });
      } else {
        await client.graphql({ query: gqlUpdate, variables: { input: { id: SETTINGS_ID, settingsJson: json } } });
      }
    } catch (e) {
      console.error('Failed to save settings to backend:', e);
    }
  },

  reset: async () => {
    const defaults = merge(null);
    set({ settings: defaults });
    applyTheme(defaults.branding);
    localStorage.removeItem(LS_KEY);

    try {
      const json = JSON.stringify(stripRuntimeFields(defaults));
      if (get().settingsExists === false) {
        await client.graphql({ query: gqlCreate, variables: { input: { id: SETTINGS_ID, settingsJson: json } } });
        set({ settingsExists: true });
      } else {
        await client.graphql({ query: gqlUpdate, variables: { input: { id: SETTINGS_ID, settingsJson: json } } });
      }
    } catch (e) {
      console.error('Failed to reset settings:', e);
    }
  },
}));

export default useShopSettings;
